import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  FileCheck, RefreshCw, AlertCircle, CheckCircle2,
  XCircle, ChevronDown, ChevronUp, Loader2, Play, Hash,
  AlertTriangle, User, Building2
} from 'lucide-react';
import { toast } from 'sonner';

// ── Formateadores ────────────────────────────────────────────────────────────
const fmtMoneda = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('es-AR', {
    style: 'currency', currency: 'ARS', maximumFractionDigits: 2
  }).format(n);
};

const fmtPorc = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toFixed(1)}%`;
};

const AprobacionCotizacionesPage = () => {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cotizaciones, setCotizaciones] = useState([]);

  // Estado por cotización: 'idle' | 'loading' | 'ok' | 'financiera' | 'sin_credito' | 'error'
  const [estados, setEstados] = useState({});
  // Resultado por cotización
  const [resultados, setResultados] = useState({});
  // Selección (checkbox) por cotización: { [id]: true }
  const [seleccionados, setSeleccionados] = useState({});
  // Expansión de filas de detalle
  const [expandidos, setExpandidos] = useState({});

  const fetchCotizaciones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/aprobacion-cotizaciones/cotizaciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      const items = data.cotizaciones || [];
      setCotizaciones(items);

      // Inicializar estados según la categoría YIQI actual
      const init = {};
      items.forEach(c => {
        init[c.ID] = c.categoria === 'aprobada' ? 'ok'
          : c.categoria === 'financiera' ? 'financiera'
          : 'idle';
      });
      setEstados(init);
      setResultados({});
      setSeleccionados({});
    } catch (err) {
      setError(err.message);
      toast.error('Error al cargar cotizaciones: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchCotizaciones();
  }, [fetchCotizaciones]);

  // Una cotización se puede seleccionar/aprobar si no está procesándose ni ya aprobada.
  const esSeleccionable = (cotId) => {
    const e = estados[cotId] || 'idle';
    return e !== 'loading' && e !== 'ok';
  };

  const aprobarCotizacion = async (cotId) => {
    setEstados(prev => ({ ...prev, [cotId]: 'loading' }));
    try {
      const res = await fetch(`${API_URL}/api/admin/aprobacion-cotizaciones/aprobar/${cotId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || `Error ${res.status}`);
      }

      const nuevoEstado = data.requiere_financiera ? 'financiera'
        : data.sin_credito ? 'sin_credito'
        : 'ok';
      setEstados(prev => ({ ...prev, [cotId]: nuevoEstado }));
      setResultados(prev => ({
        ...prev,
        [cotId]: {
          aprobaciones: data.aprobaciones,
          requiere_financiera: data.requiere_financiera,
          sin_credito: data.sin_credito,
          estado_final: data.estado_final,
          categoria_final: data.categoria_final,
          pasos: data.pasos
        }
      }));
      // Si quedó aprobada, la sacamos de la selección.
      if (nuevoEstado === 'ok') {
        setSeleccionados(prev => {
          const next = { ...prev };
          delete next[cotId];
          return next;
        });
      }

      if (data.requiere_financiera) {
        toast.warning(`Cotización #${cotId} requiere Aprobación Financiera (se detuvo)`);
      } else if (data.sin_credito) {
        toast.warning(`Cotización #${cotId} quedó en "Enviada": el cliente no tiene límite de crédito (revisión manual)`);
      } else {
        toast.success(`Cotización #${cotId} aprobada (${data.aprobaciones} aprobación/es)`);
      }
    } catch (err) {
      setEstados(prev => ({ ...prev, [cotId]: 'error' }));
      setResultados(prev => ({ ...prev, [cotId]: { error: err.message } }));
      toast.error(`Error en cotización #${cotId}: ${err.message}`);
    }
  };

  // ── Selección ──
  const toggleSeleccion = (cotId) => {
    setSeleccionados(prev => {
      const next = { ...prev };
      if (next[cotId]) delete next[cotId];
      else next[cotId] = true;
      return next;
    });
  };

  const seleccionablesIds = cotizaciones.filter(c => esSeleccionable(c.ID)).map(c => c.ID);
  const seleccionadosIds = cotizaciones.filter(c => seleccionados[c.ID]).map(c => c.ID);
  const todasSeleccionadas = seleccionablesIds.length > 0
    && seleccionablesIds.every(id => seleccionados[id]);

  const toggleSeleccionarTodas = () => {
    if (todasSeleccionadas) {
      setSeleccionados({});
    } else {
      const next = {};
      seleccionablesIds.forEach(id => { next[id] = true; });
      setSeleccionados(next);
    }
  };

  const aprobarSeleccionadas = async () => {
    const ids = seleccionadosIds.filter(id => esSeleccionable(id));
    if (ids.length === 0) {
      toast.info('No hay cotizaciones seleccionadas.');
      return;
    }
    for (const id of ids) {
      await aprobarCotizacion(id);
    }
  };

  const toggleExpand = (cotId) => {
    setExpandidos(prev => ({ ...prev, [cotId]: !prev[cotId] }));
  };

  const contadorEstados = cotizaciones.reduce((acc, c) => {
    const est = estados[c.ID] || 'idle';
    acc[est] = (acc[est] || 0) + 1;
    return acc;
  }, {});

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="bg-red-900/20 border border-red-500/50 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-200 mb-2">Error de Conexión</h2>
          <p className="text-red-300/80 mb-6">{error}</p>
          <button
            onClick={fetchCotizaciones}
            className="bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Grid de columnas compartido entre cabecera y filas.
  const GRID = 'grid grid-cols-[auto,1.1fr,1fr,1.4fr,0.9fr,0.7fr,1.2fr,auto,auto] items-center gap-3';

  return (
    <div className="text-gray-100">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ── Header ── */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-[#1A1D27] p-6 rounded-xl border border-gray-800 shadow-xl">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#00a1df] to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
              <FileCheck className="w-7 h-7 text-[#00a1df]" />
              Aprobación de Cotizaciones
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Aprobación en lote de cotizaciones comerciales
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={aprobarSeleccionadas}
              disabled={loading || seleccionadosIds.length === 0}
              className="flex items-center gap-2 bg-[#00a1df] hover:bg-[#0090c7] disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-lg shadow-lg transition-all font-black text-xs active:scale-95"
            >
              <Play className="w-4 h-4 fill-current" />
              APROBAR SELECCIONADAS{seleccionadosIds.length > 0 ? ` (${seleccionadosIds.length})` : ''}
            </button>
            <button
              onClick={fetchCotizaciones}
              disabled={loading}
              className="flex items-center justify-center w-10 h-10 bg-gray-800 hover:bg-gray-700 text-[#00a1df] border border-gray-700 rounded-lg transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Cotizaciones"
            value={loading ? '…' : cotizaciones.length}
            icon={<FileCheck className="w-5 h-5" />}
            color="text-[#00a1df]"
            bg="bg-[#00a1df]/10"
          />
          <StatCard
            label="Aprobadas"
            value={loading ? '…' : (contadorEstados.ok || 0)}
            icon={<CheckCircle2 className="w-5 h-5" />}
            color="text-green-400"
            bg="bg-green-500/10"
          />
          <StatCard
            label="Aprob. Financiera"
            value={loading ? '…' : (contadorEstados.financiera || 0)}
            icon={<AlertTriangle className="w-5 h-5" />}
            color="text-amber-400"
            bg="bg-amber-500/10"
          />
          <StatCard
            label="Con Error"
            value={loading ? '…' : (contadorEstados.error || 0)}
            icon={<XCircle className="w-5 h-5" />}
            color="text-red-400"
            bg="bg-red-500/10"
          />
        </div>

        {/* ── Lista de cotizaciones ── */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
          {/* Cabecera con seleccionar todas */}
          <div className={`${GRID} px-6 py-3 border-b border-gray-700 bg-gray-800/50 text-[10px] font-bold text-gray-400 uppercase tracking-widest`}>
            <input
              type="checkbox"
              checked={todasSeleccionadas}
              onChange={toggleSeleccionarTodas}
              disabled={seleccionablesIds.length === 0}
              title="Seleccionar todas"
              className="w-4 h-4 accent-[#00a1df] cursor-pointer disabled:cursor-not-allowed"
            />
            <span>Cotización</span>
            <span>Vendedor</span>
            <span>Cliente</span>
            <span className="text-right">Total</span>
            <span className="text-right">Rentab.</span>
            <span>Estado / Resultado</span>
            <span></span>
            <span></span>
          </div>

          {loading ? (
            <div className="py-20 flex flex-col items-center gap-4 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin text-[#00a1df]" />
              <p className="text-sm">Cargando cotizaciones...</p>
            </div>
          ) : cotizaciones.length === 0 ? (
            <div className="py-20 text-center text-gray-500">
              <FileCheck className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p>No hay cotizaciones para mostrar.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700/50">
              {cotizaciones.map((cot) => {
                const cotId = cot.ID;
                const estado = estados[cotId] || 'idle';
                const resultado = resultados[cotId];
                const isExpanded = expandidos[cotId];
                const seleccionable = esSeleccionable(cotId);

                return (
                  <div key={cotId}>
                    {/* Fila principal */}
                    <div className={`${GRID} px-6 py-4 hover:bg-gray-700/20 transition-colors`}>

                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={!!seleccionados[cotId]}
                        onChange={() => toggleSeleccion(cotId)}
                        disabled={!seleccionable}
                        className="w-4 h-4 accent-[#00a1df] cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      />

                      {/* Cotización (nro + id) */}
                      <div className="min-w-0">
                        <p className="font-bold text-white truncate">
                          {cot.nro_cotizacion || `#${cotId}`}
                        </p>
                        <p className="text-[10px] text-gray-500 font-mono uppercase">ID: {cotId}</p>
                      </div>

                      {/* Vendedor */}
                      <div className="min-w-0 flex items-center gap-1.5 text-gray-300">
                        <User className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                        <span className="text-sm truncate" title={cot.vendedor || ''}>
                          {cot.vendedor || '—'}
                        </span>
                      </div>

                      {/* Cliente */}
                      <div className="min-w-0 flex items-center gap-1.5 text-gray-300">
                        <Building2 className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                        <span className="text-sm truncate" title={cot.cliente || ''}>
                          {cot.cliente || '—'}
                        </span>
                      </div>

                      {/* Total */}
                      <div className="text-right text-sm font-semibold text-gray-100 tabular-nums">
                        {fmtMoneda(cot.total)}
                      </div>

                      {/* Rentabilidad */}
                      <div className={`text-right text-sm font-bold tabular-nums ${
                        cot.rentabilidad == null ? 'text-gray-500'
                          : Number(cot.rentabilidad) < 15 ? 'text-amber-400'
                          : 'text-green-400'
                      }`}>
                        {fmtPorc(cot.rentabilidad)}
                      </div>

                      {/* Estado / Resultado del proceso */}
                      <div className="min-w-0">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider truncate" title={cot.estado}>
                          {cot.estado || '—'}
                        </p>
                        {estado === 'idle' && (
                          <span className="text-xs text-gray-500 italic">— pendiente —</span>
                        )}
                        {estado === 'loading' && (
                          <span className="flex items-center gap-1.5 text-xs text-[#00a1df]">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Aprobando...
                          </span>
                        )}
                        {estado === 'ok' && (
                          <span className="flex items-center gap-1 text-xs text-green-400 font-bold">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Aprobada{resultado ? ` · ${resultado.estado_final}` : ''}
                          </span>
                        )}
                        {estado === 'financiera' && (
                          <span className="flex items-center gap-1 text-xs text-amber-400 font-bold">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Aprob. Financiera
                          </span>
                        )}
                        {estado === 'sin_credito' && (
                          <span className="flex items-center gap-1 text-xs text-amber-400 font-bold">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Sin crédito · Enviada
                          </span>
                        )}
                        {estado === 'error' && resultado && (
                          <span className="flex items-center gap-1 text-xs text-red-400 font-bold" title={resultado.error}>
                            <XCircle className="w-3.5 h-3.5" />
                            Error
                          </span>
                        )}
                      </div>

                      {/* Botón aprobar */}
                      <button
                        onClick={() => aprobarCotizacion(cotId)}
                        disabled={estado === 'loading' || estado === 'ok'}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all active:scale-95 ${
                          estado === 'ok'
                            ? 'bg-green-900/30 text-green-400 border border-green-500/20 cursor-default'
                            : estado === 'loading'
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : estado === 'financiera' || estado === 'sin_credito'
                            ? 'bg-amber-600 hover:bg-amber-500 text-white'
                            : estado === 'error'
                            ? 'bg-red-600 hover:bg-red-500 text-white'
                            : 'bg-[#00a1df] hover:bg-[#0090c7] text-white shadow-lg shadow-[#00a1df]/20'
                        }`}
                      >
                        {estado === 'loading' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : estado === 'ok' ? (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        ) : (
                          <FileCheck className="w-3.5 h-3.5" />
                        )}
                        {estado === 'ok' ? 'Aprobada'
                          : estado === 'financiera' ? 'Reintentar'
                          : estado === 'sin_credito' ? 'Reintentar'
                          : estado === 'error' ? 'Reintentar'
                          : 'Aprobar'}
                      </button>

                      {/* Toggle detalle */}
                      <button
                        onClick={() => toggleExpand(cotId)}
                        className="p-2 text-gray-500 hover:text-gray-300 rounded-lg hover:bg-gray-700/40 transition-colors"
                        title="Ver detalle"
                      >
                        {isExpanded
                          ? <ChevronUp className="w-4 h-4" />
                          : <ChevronDown className="w-4 h-4" />
                        }
                      </button>
                    </div>

                    {/* Detalle expandible */}
                    {isExpanded && (
                      <div className="bg-gray-900/60 border-t border-gray-700/50 px-6 py-4">
                        {resultado && resultado.pasos ? (
                          <>
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold mb-3 flex items-center gap-2">
                              <Hash className="w-3 h-3" />
                              Pasos del proceso de aprobación
                            </p>
                            <ol className="space-y-1.5 text-xs text-gray-300 list-decimal list-inside">
                              {resultado.pasos.map((paso, i) => (
                                <li key={i} className="text-gray-400">{paso}</li>
                              ))}
                            </ol>
                          </>
                        ) : (
                          <>
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold mb-3 flex items-center gap-2">
                              <Hash className="w-3 h-3" />
                              Datos de la cotización
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <tbody className="divide-y divide-gray-800">
                                  {Object.entries(cot.row || {}).map(([k, v]) => (
                                    <tr key={k} className="hover:bg-gray-800/40 transition-colors">
                                      <td className="py-1.5 pr-4 font-mono text-cyan-400 align-top whitespace-nowrap">{k}</td>
                                      <td className="py-1.5 text-gray-300 break-all">
                                        {v === null || v === '' ? '—' : String(v)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Info Footer ── */}
        <div className="bg-blue-900/10 border border-blue-500/20 rounded-lg p-4 flex gap-3 text-sm text-blue-300/80">
          <Hash className="w-5 h-5 shrink-0 mt-0.5 text-blue-400" />
          <p>
            Los datos provienen de las smarties de cotizaciones (Schema <strong className="text-blue-300">1383</strong> · Smarties <strong className="text-blue-300">3242</strong> y <strong className="text-blue-300">3243</strong>).
            La aprobación lee las transiciones disponibles de cada cotización (GetInstance) y ejecuta el <code className="text-blue-300 bg-blue-900/30 px-1 rounded">transitionId</code> correcto:
            desde <code className="text-blue-300 bg-blue-900/30 px-1 rounded">Aprobación especial</code> hace <strong>Aprobar</strong>, y si cae en <code className="text-blue-300 bg-blue-900/30 px-1 rounded">Enviada</code> la vuelve a aprobar hasta llegar a <code className="text-blue-300 bg-blue-900/30 px-1 rounded">Aprobada</code>.
            Si pasa a <strong className="text-amber-300">Aprobación Financiera</strong>, o si al aprobar el cliente <strong className="text-amber-300">no tiene límite de crédito</strong>, el proceso se detiene para revisión manual (no se reintenta).
          </p>
        </div>

      </div>
    </div>
  );
};

// ── Componente auxiliar ──────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color, bg }) => (
  <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 backdrop-blur-sm">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-gray-400 text-sm">{label}</p>
        <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
      </div>
      <div className={`${bg} p-2 rounded-lg ${color}`}>{icon}</div>
    </div>
  </div>
);

export default AprobacionCotizacionesPage;
